import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp, Search, Sparkles, GripVertical, Pin, Calculator } from "lucide-react";
import { HELP_CONTENT } from "../components/HelpButton";

// Newest features surfaced at the top of the Help Hub. Each item is a bite-sized
// hint pointing to where the feature lives. Update this list as new features
// ship so users discover them without a changelog email.
const WHATS_NEW = [
  {
    icon: Calculator,
    title: "Running total on Bulk Add",
    body: "See a live net (+/−) as you enter rows. Turns red if the total would overdraw your account.",
    to: "/transactions/bulk-add",
    cta: "Try Bulk Add",
  },
  {
    icon: Pin,
    title: "Pin transactions in Register",
    body: "Tap the pin icon on any row to float it to the top while you audit. Drag pinned rows to reorder.",
    to: "/register",
    cta: "Open Register",
  },
  {
    icon: GripVertical,
    title: "Drag-to-reorder Bulk Add rows",
    body: "Grab the handle on the left of any row to reorder — great when a template loads rows in the wrong sequence.",
    to: "/transactions/bulk-add",
    cta: "Try it",
  },
];

/**
 * /help — global help hub.
 *
 * Aggregates every page-specific HELP_CONTENT entry into one browsable list so
 * users can find any answer from the bottom nav without knowing which page a
 * given tip lives on. Each topic starts collapsed; opening one keeps others
 * collapsed to keep the phone screen scannable.
 */
export default function Help() {
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState("");

  const topics = Object.entries(HELP_CONTENT).map(([id, entry]) => ({
    id,
    title: entry.title,
    sections: entry.sections,
  }));

  const q = query.trim().toLowerCase();
  const filtered = q
    ? topics.filter((t) => {
        if (t.title.toLowerCase().includes(q)) return true;
        return t.sections.some((s) => {
          const heading = (s.heading || "").toLowerCase();
          const text = Array.isArray(s.text)
            ? s.text.join(" ").toLowerCase()
            : (s.text || "").toLowerCase();
          return heading.includes(q) || text.includes(q);
        });
      })
    : topics;

  return (
    <div className="space-y-4" data-testid="help-page">
      <div className="flex items-center justify-between">
        <Link
          to="/"
          data-testid="help-back"
          className="nb-btn nb-btn-secondary !py-2 !px-3"
        >
          <ArrowLeft size={16} strokeWidth={3} /> Home
        </Link>
        <h1 className="font-display text-2xl font-black flex items-center gap-2">
          <HelpCircle size={22} strokeWidth={3} />
          Help
        </h1>
        <div className="w-[70px]" />
      </div>

      <div className="nb-card p-3 space-y-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-[#4a4a4a]">
          Every SyncBook feature — how it works
        </div>
        <div className="relative">
          <Search
            size={14}
            strokeWidth={3}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a4a4a]"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help (e.g., ‘reconcile’, ‘csv’, ‘bulk’)…"
            data-testid="help-search"
            className="nb-input !pl-9 !py-2 text-sm"
          />
        </div>
      </div>

      {/* What's New — hidden while searching so the search results stay clean */}
      {!q && (
        <div
          className="nb-card-lg p-4 space-y-3"
          style={{ background: "#fff3c4" }}
          data-testid="whats-new"
        >
          <div className="flex items-center gap-2">
            <Sparkles size={16} strokeWidth={3} />
            <div className="font-display font-black text-base">What&apos;s new</div>
            <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#1a1a1a] text-white">
              Just shipped
            </span>
          </div>
          <div className="space-y-2">
            {WHATS_NEW.map((item, idx) => {
              const Icon = item.icon;
              return (
                <Link
                  key={idx}
                  to={item.to}
                  data-testid={`whats-new-item-${idx}`}
                  className="flex items-start gap-3 p-3 rounded-xl bg-white border-2 border-[#1a1a1a] hover:bg-[#f4f0eb] transition"
                >
                  <div className="w-8 h-8 rounded-lg border-2 border-[#1a1a1a] flex items-center justify-center shrink-0 bg-[#d9d4f1]">
                    <Icon size={14} strokeWidth={3} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-black text-sm">
                        {item.title}
                      </div>
                      <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#b5e4ca] text-[#1a1a1a]">
                        New
                      </span>
                    </div>
                    <div className="text-[12px] font-medium text-[#4a4a4a] leading-snug mt-0.5">
                      {item.body}
                    </div>
                    <div className="text-[11px] font-bold underline text-[#1a1a1a] mt-1">
                      {item.cta} →
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="help-topics">
        {filtered.length === 0 ? (
          <div
            className="nb-card p-4 text-sm font-medium text-[#4a4a4a] text-center"
            data-testid="help-no-results"
          >
            No results for “{query}”. Try a different word — or clear the search
            to see every topic.
          </div>
        ) : (
          filtered.map((t) => {
            const isOpen = openId === t.id;
            return (
              <div
                key={t.id}
                className="nb-card overflow-hidden"
                data-testid={`help-topic-${t.id}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : t.id)}
                  data-testid={`help-topic-toggle-${t.id}`}
                  className="w-full flex items-center justify-between gap-2 p-3 text-left"
                >
                  <div className="font-display font-black text-base">
                    {t.title}
                  </div>
                  {isOpen ? (
                    <ChevronUp size={16} strokeWidth={3} />
                  ) : (
                    <ChevronDown size={16} strokeWidth={3} />
                  )}
                </button>
                {isOpen && (
                  <div className="p-3 pt-0 space-y-3 border-t-2 border-[#1a1a1a]/10">
                    {t.sections.map((s, i) => (
                      <div key={i} className="space-y-1">
                        {s.heading && (
                          <div className="font-display font-black text-sm">
                            {s.heading}
                          </div>
                        )}
                        {Array.isArray(s.text) ? (
                          <ul className="list-disc pl-5 space-y-1">
                            {s.text.map((t2, j) => (
                              <li
                                key={j}
                                className="text-[13px] font-medium leading-snug"
                              >
                                {t2}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-[13px] font-medium leading-snug">
                            {s.text}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="nb-card p-4 space-y-1 text-xs font-medium text-[#4a4a4a]">
        <div className="font-display font-black text-sm text-[#1a1a1a]">
          Still stuck?
        </div>
        <div>
          Every page also has a ? button in its header for that page&apos;s
          quick tips. If something feels broken, log in on a laptop and email
          the SyncBook team from Profile.
        </div>
      </div>
    </div>
  );
}
